import React, { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { NotFoundException } from '@zxing/library';

const successSound = new Audio('https://freesound.org/data/previews/512/512983_7036326-lq.mp3');

const BarcodeInventoryScanner = ({ filteredProducts, selectedRevenueId, onProductScanned }) => {
  const videoRef = useRef(null);
  const codeReaderRef = useRef(null);
  const streamRef = useRef(null);
  const lastScannedRef = useRef({ code: '', timestamp: 0 });
  const isMountedRef = useRef(true);
  const isDecodingRef = useRef(false);
  const inputRef = useRef(null);
  
  const [scannedCode, setScannedCode] = useState('');
  const [product, setProduct] = useState(null);
  const [error, setError] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [useCamera, setUseCamera] = useState(true);
  const [barcodeInput, setBarcodeInput] = useState('');

  const playSuccessSound = () => {
    successSound.volume = 0.7;
    successSound.play().catch(err => {
      console.error('Error al reproducir sonido:', err.message);
    });
  };

  const findProductByCode = (code) => {
    const now = Date.now();
    if (code === lastScannedRef.current.code && now - lastScannedRef.current.timestamp < 2000) {
      console.log('Escaneo duplicado ignorado:', code);
      return null;
    }

    try {
      const decodedCode = decodeURIComponent(code.trim());
      console.log('Buscando producto con código:', decodedCode);
      const exactMatch = filteredProducts.find(item => {
        const itemCode = item.code ? String(item.code) : '';
        return itemCode === decodedCode;
      });
      if (exactMatch) {
        console.log('Producto encontrado por código:', exactMatch);
        return exactMatch;
      }
      
      if (!isNaN(decodedCode)) {
        const idMatch = filteredProducts.find(item => String(item.id) === decodedCode);
        if (idMatch) {
          console.log('Producto encontrado por ID:', idMatch);
          return idMatch;
        }
      }
      
      const nameMatch = filteredProducts.find(item => 
        item.name && decodedCode.toLowerCase().includes(item.name.toLowerCase())
      );
      if (nameMatch) {
        console.log('Producto encontrado por nombre:', nameMatch);
        return nameMatch;
      }
      
      console.warn('No se encontró producto para el código:', decodedCode);
      return null;
    } catch (e) {
      console.error('Error al decodificar producto:', e);
      return null;
    }
  };

  const safelyStopStream = () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
        videoRef.current.onloadedmetadata = null;
        videoRef.current.onerror = null;
      }
    } catch (err) {
      console.error('Error al detener el stream:', err);
    }
  };

  const stopScanner = () => {
    isDecodingRef.current = false;
    safelyStopStream();
    if (codeReaderRef.current) {
      try {
        codeReaderRef.current.reset();
      } catch (e) {
        console.error('Error al reiniciar el lector de códigos:', e);
      }
    }
  };

  const startVideoPlayback = async () => {
    if (!videoRef.current) {
      throw new Error('Elemento de video no disponible');
    }

    try {
      await new Promise((resolve, reject) => {
        const checkReady = () => {
          if (!isMountedRef.current || !videoRef.current) {
            resolve();
            return;
          }
          if (videoRef.current.readyState >= 2) {
            resolve();
          } else {
            videoRef.current.onloadedmetadata = () => resolve();
            videoRef.current.onerror = reject;
            setTimeout(checkReady, 100);
          }
        };
        checkReady();
      });

      if (videoRef.current) {
        await videoRef.current.play().catch(async (err) => {
          console.warn('Primer intento de reproducción fallido, reintentando:', err.message);
          await new Promise(resolve => setTimeout(resolve, 300));
          if (videoRef.current) {
            await videoRef.current.play().catch(secondErr => {
              console.error('Segundo intento de reproducción fallido:', secondErr.message);
              throw secondErr;
            });
          }
        });
      }
    } catch (err) {
      console.error('Error de reproducción de video:', err.message);
      throw err;
    }
  };

  const startScanner = async () => {
    if (!isMountedRef.current) return;

    try {
      setIsScanning(true);
      setError('');
      stopScanner();

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      }).catch(err => {
        throw new Error(`Acceso a cámara denegado: ${err.message}`);
      });

      if (!isMountedRef.current) {
        safelyStopStream();
        return;
      }

      streamRef.current = stream;
      if (!videoRef.current) {
        throw new Error('Elemento de video no encontrado');
      }

      videoRef.current.srcObject = stream;
      videoRef.current.playsInline = true;
      videoRef.current.muted = true;

      await startVideoPlayback();

      if (!codeReaderRef.current || !isMountedRef.current) {
        safelyStopStream();
        return;
      }

      isDecodingRef.current = true;

      const decodeCallback = (result, err) => {
        if (!isMountedRef.current || !isDecodingRef.current) return;
        
        if (result) {
          const code = result.getText();
          console.log('Código escaneado por cámara:', code);
          processBarcode(code);
        }
        
        if (err && !(err instanceof NotFoundException)) {
          console.error('Error al leer código de barras:', err.message);
          if (isMountedRef.current) {
            setError('Error leyendo código de barras');
          }
        }
      };

      codeReaderRef.current.decodeFromVideoElement(videoRef.current, decodeCallback);

    } catch (err) {
      console.error('Error del escáner:', err.message);
      if (isMountedRef.current) {
        setError(err.message || 'No se pudo iniciar el escáner');
        setIsScanning(false);
        setUseCamera(false);
      }
      safelyStopStream();
    }
  };

  const processBarcode = (code) => {
    if (!code) {
      console.warn('No se proporcionó código para procesar');
      setError('No se proporcionó código');
      return;
    }

    let cleanCode = code.trim();
    // Eliminar el prefijo 'Backspace' si está presente
    if (cleanCode.startsWith('Backspace')) {
      cleanCode = cleanCode.replace('Backspace', '');
      console.log('Prefijo Backspace eliminado, código limpio:', cleanCode);
    } else {
      console.log('No se detectó prefijo Backspace');
    }

    // También manejar caracteres de control ASCII (como \b)
    cleanCode = cleanCode.replace(/\b/g, '');
    console.log('Código después de eliminar caracteres de control:', cleanCode);

    console.log('Procesando código de barras:', cleanCode);
    setScannedCode(cleanCode);
    const foundProduct = findProductByCode(cleanCode);
    
    if (foundProduct) {
      const now = Date.now();
      lastScannedRef.current = { code: cleanCode, timestamp: now };
      
      setProduct(foundProduct);
      setError('✓ Producto añadido al pedido');
      
      playSuccessSound();
      
      try {
        const productData = {
          id: foundProduct.id,
          name: foundProduct.name,
          price: foundProduct.price,
          quantity: 1,
          modifiers: foundProduct.modifiers || [],
          tax: foundProduct.tax || 0,
          cargo: foundProduct.cargo || 0,
          isFromDB: false,
          code: foundProduct.code || null,
          KDS: foundProduct.KDS || [],
          PRT: foundProduct.PRT || [],
        };
        console.log('Enviando a onProductScanned:', productData);
        onProductScanned(productData);
      } catch (callbackErr) {
        console.error('Error en callback onProductScanned:', callbackErr.message);
        setError('Error al procesar el producto escaneado');
      }
    } else if (cleanCode !== lastScannedRef.current.code) {
      setProduct(null);
      setError('Código sin producto');
    }
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (useCamera) return;

      // Prevenir comportamiento predeterminado del navegador
      event.preventDefault();

      console.log('Tecla recibida:', event.key);

      if (event.key !== 'Enter') {
        setBarcodeInput((prev) => {
          const newInput = prev + event.key;
          console.log('barcodeInput actual:', newInput);
          return newInput;
        });
      } else if (event.key === 'Enter' && barcodeInput) {
        console.log('Entrada completa del escáner:', barcodeInput);
        processBarcode(barcodeInput);
        setBarcodeInput('');
        // Mantener el enfoque en el campo de entrada
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [useCamera, barcodeInput]);

  // Asegurar que el campo de entrada esté enfocado en modo teclado
  useEffect(() => {
    if (!useCamera && inputRef.current) {
      console.log('Enfocando campo de entrada');
      inputRef.current.focus();
    }
  }, [useCamera]);

  useEffect(() => {
    isMountedRef.current = true;
    codeReaderRef.current = new BrowserMultiFormatReader();

    if (filteredProducts.length > 0 && selectedRevenueId) {
      startScanner().catch(err => {
        console.error('Error no manejado del escáner:', err.message);
        if (isMountedRef.current) {
          setError('Error crítico del escáner');
          setUseCamera(false);
        }
      });
    } else {
      setError('Esperando productos del centro de ingresos seleccionado...');
      setUseCamera(false);
    }

    return () => {
      isMountedRef.current = false;
      stopScanner();
    };
  }, [filteredProducts, selectedRevenueId]);

  const styles = {
    container: {
      padding: '20px',
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
      marginBottom: '20px'
    },
    title: {
      color: '#1a3c34',
      fontSize: '1.5rem',
      fontWeight: '700',
      marginBottom: '15px'
    },
    videoContainer: {
      position: 'relative',
      width: '100%',
      maxWidth: '400px',
      margin: '0 auto',
      borderRadius: '10px',
      overflow: 'hidden',
      boxShadow: '0 3px 15px rgba(0, 0, 0, 0.08)',
      display: useCamera ? 'block' : 'none'
    },
    video: {
      width: '100%',
      backgroundColor: '#f0f0f0',
      display: 'block'
    },
    inputContainer: {
      width: '100%',
      maxWidth: '400px',
      margin: '0 auto',
      display: useCamera ? 'none' : 'block'
    },
    barcodeInput: {
      width: '100%',
      padding: '10px',
      fontSize: '1rem',
      borderRadius: '8px',
      border: '1px solid #ccc',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      outline: 'none'
    },
    statusContainer: {
      marginTop: '20px',
      padding: '15px',
      backgroundColor: '#f8f9fa',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
    },
    scannedCode: {
      fontSize: '0.95rem',
      color: '#333',
      fontWeight: '600',
      marginBottom: '10px'
    },
    productInfo: {
      marginTop: '10px'
    },
    productName: {
      fontWeight: '600',
      color: '#1a3c34'
    },
    productPrice: {
      color: '#2e5b52',
      margin: '5px 0'
    },
    successMessage: {
      color: '#16a085',
      fontWeight: '600'
    },
    errorMessage: {
      color: '#e74c3c',
      fontWeight: '600'
    },
    loadingMessage: {
      color: '#3498db',
      fontWeight: '600'
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Escáner de Código de Barras</h2>
      <div style={styles.videoContainer}>
        <video 
          ref={videoRef}
          style={styles.video}
          muted
          playsInline
        />
      </div>
      <div style={styles.inputContainer} onClick={() => inputRef.current?.focus()}>
        <input
          ref={inputRef}
          type="text"
          value={barcodeInput}
          onChange={(e) => setBarcodeInput(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && barcodeInput) {
              console.log('Entrada manual enviada:', barcodeInput);
              processBarcode(barcodeInput);
              setBarcodeInput('');
              if (inputRef.current) {
                inputRef.current.focus();
              }
            }
          }}
          placeholder="Escanea o ingresa el código de barras"
          style={styles.barcodeInput}
          autoFocus={!useCamera}
          disabled={useCamera}
        />
      </div>
      <div style={styles.statusContainer}>
        <p style={styles.scannedCode}>Código escaneado: {scannedCode || '---'}</p>
        {isScanning || !useCamera ? (
          product ? (
            <div style={styles.productInfo}>
              <p style={styles.productName}><strong>Producto:</strong> {product.name}</p>
              <p style={styles.productPrice}><strong>Precio:</strong> ${product.price.toFixed(2)}</p>
              <p style={styles.successMessage}>{error || '✓ Producto añadido al pedido'}</p>
            </div>
          ) : (
            error && <p style={styles.errorMessage}>{error}</p>
          )
        ) : (
          <p style={error ? styles.errorMessage : styles.loadingMessage}>
            {error || 'Iniciando escáner...'}
          </p>
        )}
      </div>
    </div>
  );
};

export default BarcodeInventoryScanner;